import { AskWrennenContext, AskWrennenMessage } from '../types';

interface AskWrennenResponse {
  answer: string;
}

const isDev = typeof window !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname);
const parseJsonSafe = (rawText: string) => {
  try {
    return rawText ? JSON.parse(rawText) : {};
  } catch {
    return { rawText };
  }
};

export const askWrennen = async (
  question: string,
  context: AskWrennenContext,
  history: AskWrennenMessage[]
): Promise<AskWrennenResponse> => {
  const payload = {
    question,
    context,
    history
  };

  if (isDev) {
    console.info('[Ask Wrennen] Outgoing request payload', payload);
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 25000);

  try {
    const response = await fetch('/api/ask-wrennen', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });

    const rawText = await response.text();
    const parsed = parseJsonSafe(rawText);

    if (isDev) {
      console.info('[Ask Wrennen] API response', {
        ok: response.ok,
        status: response.status,
        body: parsed
      });
    }

    if (!response.ok) {
      throw new Error(parsed?.error || (isDev ? 'Ask Wrennen request failed.' : 'Ask Wrennen could not reach the AI service right now.'));
    }

    const answer = typeof parsed?.answer === 'string' ? parsed.answer.trim() : '';
    if (!answer) {
      throw new Error(isDev ? 'Ask Wrennen returned an empty answer.' : 'Ask Wrennen could not produce an answer right now.');
    }

    return { answer };
  } catch (error) {
    if (isDev) {
      console.error('[Ask Wrennen] Frontend request failed', error);
    }

    if (error instanceof Error && (error.name === 'AbortError' || error.name === 'TimeoutError')) {
      throw new Error(isDev ? 'Ask Wrennen timed out while waiting for the AI provider.' : 'Ask Wrennen timed out while reaching the AI service.');
    }

    throw error instanceof Error
      ? error
      : new Error(isDev ? 'Ask Wrennen failed unexpectedly on the client.' : 'Ask Wrennen could not respond right now.');
  } finally {
    window.clearTimeout(timeoutId);
  }
};
